import { NavLink } from 'react-router';
import { Users, Network, ShieldCheck, Laptop } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

interface SidebarLinkProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  testId?: string;
}

function SidebarLink({ to, icon: Icon, children, testId }: SidebarLinkProps) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'cursor-pointer',
          isActive
            ? 'bg-sidebar-active text-foreground'
            : 'text-muted-foreground hover:bg-panel-hover',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{children}</span>
    </NavLink>
  );
}

export function StaffSidebar() {
  const adminUser = useAuthStore((s) => s.adminUser);
  const isRoot = adminUser?.role === 'root';

  return (
    <aside
      className="flex h-full w-60 shrink-0 flex-col border-r bg-muted"
      data-testid="staff-sidebar"
    >
      <div className="flex h-14 items-center border-b px-4">
        <span className="text-sm font-semibold text-foreground">{'OptiSigns Staff'}</span>
      </div>

      <nav className="flex flex-col gap-1 p-3">
        <SidebarLink to="/staff/customers" icon={Users} testId="nav-customers">
          {'Customers'}
        </SidebarLink>

        <div className="mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {'Network'}
        </div>
        <SidebarLink to="/staff/network/peers" icon={Network} testId="nav-peers">
          {'Peers'}
        </SidebarLink>
        <SidebarLink to="/staff/network/subnets" icon={Network} testId="nav-subnets">
          {'Subnets'}
        </SidebarLink>

        <div className="mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {'My Account'}
        </div>
        <SidebarLink to="/staff/enroll" icon={Laptop} testId="nav-enroll">
          {'Enroll my laptop'}
        </SidebarLink>

        {isRoot ? (
          <>
            <div className="mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {'Administration'}
            </div>
            <SidebarLink to="/staff/admin-users" icon={ShieldCheck} testId="nav-admin-users">
              {'Admin Users'}
            </SidebarLink>
          </>
        ) : null}
      </nav>
    </aside>
  );
}
